import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import apiClient from '../api/api-client'
import { AxiosResponse, isAxiosError } from 'axios'

import { type KennzahlStudiengang, type KennzahlStudiengangCollection } from '../model'
import { useLoading } from '@/js/utils/useLoading'

export type KennzahlStudiengangPage = {
    items: Array<KennzahlStudiengang>
    totalItems: number
}

export type KennzahlStudiengangFetchParams = {
    page: number
    itemsPerPage: number
    /** Server-side label-Search (partial match). Empty string = kein Filter. */
    label?: string
    /** Filter auf Fachbereich-ID (exakt). undefined = alle Fachbereiche. */
    fachbereichId?: string
    /** Optionaler Sort, z.B. { label: 'asc' }. */
    order?: Record<string, 'asc' | 'desc'>
    /** AbortSignal — bei neuer Suche/Pagination wird die laufende Anfrage abgebrochen. */
    signal?: AbortSignal
}

/**
 * Kennzahl-Sicht eines Studiengangs: pro Studiengang erscheinen bis zu zwei
 * Einträge in der Auswahl — eine je Studienform-Charakteristik (dual / nicht-dual).
 *
 * Zwei Use-Cases:
 *  - Dashboard-Dropdown lädt einmal die volle Liste via fetchKennzahlStudiengaenge().
 *  - Verwaltungstabelle nutzt fetchKennzahlStudiengaengePaginated() mit
 *    Pagination/Suche/Abort und behält den eigenen lokalen Result-State (gibt das
 *    Ergebnis direkt zurück, damit der Caller die Items lokal rendert).
 */
export const useKennzahlStudiengangStore = defineStore('kennzahlStudiengang', () => {
    const { isLoading, setLoading, errors, hasError } = useLoading()
    const kennzahlStudiengaenge = ref<Array<KennzahlStudiengang>>([])

    // Pro Aufruf eindeutige Methoden-Bezeichnung, damit useLoading mit überlappenden
    // Aufrufen (Abort + sofortiger Restart durch neue Suche / Pagination) korrekt umgeht
    // — sonst würde derselbe Set-Eintrag den noch laufenden Folge-Request verfrüht abräumen.
    let paginatedRequestCounter = 0

    const getKennzahlStudiengaenge = computed(() => kennzahlStudiengaenge.value)

    const getKennzahlStudiengangByStudiengangIdAndIsDual = computed(() => {
        return (studiengangId: string, isDual: boolean) => {
            return kennzahlStudiengaenge.value.find(
                (entry) => entry.studiengangId === studiengangId && entry.isDual === isDual
            )
        }
    })

    async function fetchKennzahlStudiengaenge(): Promise<void> {
        await setLoading('fetching kennzahlStudiengaenge', async () => {
            const url = '/api/kennzahl-studiengaenge'
            const response: AxiosResponse<KennzahlStudiengangCollection, any> = await apiClient.get(url, {
                params: { pagination: false }
            })
            kennzahlStudiengaenge.value = response.data['hydra:member']
        })
    }

    /**
     * Server-paginated fetch für die Verwaltungstabelle.
     * Liefert null zurück, wenn die Anfrage abgebrochen wurde (durch AbortController) —
     * der Caller behält dann seinen vorigen State.
     */
    async function fetchKennzahlStudiengaengePaginated(
        params: KennzahlStudiengangFetchParams
    ): Promise<KennzahlStudiengangPage | null> {
        const queryParams: Record<string, string | number> = {
            page: params.page,
            itemsPerPage: params.itemsPerPage
        }
        if (params.label) {
            queryParams.label = params.label
        }
        if (params.fachbereichId) {
            queryParams.fachbereichId = params.fachbereichId
        }
        if (params.order) {
            for (const [field, direction] of Object.entries(params.order)) {
                queryParams[`order[${field}]`] = direction
            }
        }

        const methodName = `fetchKennzahlStudiengaengePaginated#${++paginatedRequestCounter}`

        return await setLoading(methodName, async () => {
            try {
                const response: AxiosResponse<KennzahlStudiengangCollection, any> = await apiClient.get(
                    '/api/kennzahl-studiengaenge',
                    {
                        params: queryParams,
                        signal: params.signal
                    }
                )

                return {
                    items: response.data['hydra:member'],
                    totalItems: response.data['hydra:totalItems'] ?? 0
                }
            } catch (error) {
                // Abort = der Caller hat eine neue Anfrage gestartet. Kein Fehler-Eintrag
                // ins useLoading-State, kein Re-Throw — null reicht.
                if (isAxiosError(error) && error.code === 'ERR_CANCELED') {
                    return null
                }
                throw error
            }
        })
    }

    return {
        kennzahlStudiengaenge,
        isLoading,
        errors,
        hasError,
        getKennzahlStudiengaenge,
        getKennzahlStudiengangByStudiengangIdAndIsDual,
        fetchKennzahlStudiengaenge,
        fetchKennzahlStudiengaengePaginated
    }
})
